import { Directory, File, Paths } from 'expo-file-system';
import { Platform } from 'react-native';

// iOS-only on-disk cache for LTO hero videos. Works around
// https://github.com/expo/expo/issues/43957 — expo-video's built-in iOS cache
// causes videos to reach `readyToPlay` but never paint a frame (black surface),
// so we roll our own: download to `Paths.cache` on one launch, then hand a
// `file://` URI to expo-video on the next launch → instant paint from disk,
// no network round trip, no expo-video cache proxy involved.
//
// Android sticks with expo-video's built-in caching (works fine there), so
// every function here is a no-op on non-iOS platforms.
//
// Cache invalidation is content-addressed for free: Sanity CDN URLs end in a
// hash of the video bytes (…/7a28072b496c…mp4), so `heroCacheFile(url)` maps
// same-content URLs to the same local filename and different-content URLs to
// different files. When CMS rotates the LTO campaign, `pruneStaleHeroCache`
// deletes the previous campaign's file.

const CACHE_SUBDIR = 'hero-video';

// Maps an LTO URL to a `File` in our cache subdir. Returns null on non-iOS
// (Android uses expo-video's own cache) or if the URL has no filename segment.
function heroCacheFile(url: string): File | null {
  if (Platform.OS !== 'ios') return null;
  const trailing = url.substring(url.lastIndexOf('/') + 1);
  if (!trailing) return null;
  // Filename is Sanity's content hash + extension by default; the sanitize
  // step is only a safety net for non-Sanity URLs so we can't ever write
  // outside our own subdir.
  const filename = trailing.replace(/[^a-zA-Z0-9._-]/g, '_');
  return new File(Paths.cache, CACHE_SUBDIR, filename);
}

// Sync — safe to call from `resolveHeroVideoSource` on the render path.
// Returns a `file://` URI when we have this LTO cached, so the video player
// reads it straight from disk instead of the network.
export function getCachedHeroVideoUri(url: string): string | null {
  try {
    const file = heroCacheFile(url);
    if (!file) return null;
    return file.exists ? file.uri : null;
  } catch {
    // File-system access shouldn't fail here, but if it does we degrade to
    // "not cached" and the player fetches from the remote URL as before.
    return null;
  }
}

// Concurrent-call guard so a fast persister rehydrate + CMS resolve don't
// both spawn a download for the same URL.
const inFlightWarmups = new Map<string, Promise<void>>();

// Fire-and-forget download to seed the cache for a future launch. Idempotent:
// no-ops if the file is already on disk, dedupes concurrent calls, and
// swallows any error (worst case: next launch retries and plays from network).
export async function warmHeroVideoCache(url: string): Promise<void> {
  if (Platform.OS !== 'ios') return;
  const file = heroCacheFile(url);
  if (!file || file.exists) return;
  const existing = inFlightWarmups.get(url);
  if (existing) return existing;
  const download = (async () => {
    try {
      const dir = new Directory(Paths.cache, CACHE_SUBDIR);
      dir.create({ intermediates: true, idempotent: true });
      // On iOS, `downloadFileAsync` writes to a temp location first and only
      // moves the file into place on success — no partial file to clean up on
      // failure.
      await File.downloadFileAsync(url, file, { idempotent: true });
    } catch {
      // Best-effort cache. A failure here just means the next launch pays
      // the network cost again — we never surface it to the user.
      return;
    } finally {
      inFlightWarmups.delete(url);
    }
  })();
  inFlightWarmups.set(url, download);
  return download;
}

// Diagnostics-only snapshot of what's currently on disk. Returned as plain
// data so the diagnostics UI (in a different workspace) can render it without
// pulling in file-system APIs itself. Empty on non-iOS platforms.
export type HeroVideoCacheEntry = { name: string; sizeBytes: number };
export type HeroVideoCacheSnapshot = {
  dirExists: boolean;
  files: HeroVideoCacheEntry[];
};

export function inspectHeroVideoCache(): HeroVideoCacheSnapshot {
  if (Platform.OS !== 'ios') return { dirExists: false, files: [] };
  try {
    const dir = new Directory(Paths.cache, CACHE_SUBDIR);
    if (!dir.exists) return { dirExists: false, files: [] };
    const files = dir
      .list()
      .filter((entry): entry is File => entry instanceof File)
      .map(file => ({ name: file.name, sizeBytes: file.size ?? 0 }));
    return { dirExists: true, files };
  } catch {
    return { dirExists: false, files: [] };
  }
}

// Diagnostics/dev tool — wipes the entire hero video cache directory in one
// shot. Meant to be invoked from the Diagnostics screen to force the next
// launch onto the cold-path (LTO from network) for testing. Returns whether
// the delete succeeded; a false return generally means the OS already
// evicted the directory, which is the desired end state anyway.
//
// Safe to call even while a player is reading a cached file: iOS keeps the
// inode alive until the last file descriptor closes, so playback in the
// current session continues from memory / the still-open handle. The next
// launch cold-starts.
export function clearHeroVideoCache(): boolean {
  if (Platform.OS !== 'ios') return true;
  try {
    const dir = new Directory(Paths.cache, CACHE_SUBDIR);
    if (dir.exists) dir.delete();
    return true;
  } catch {
    return false;
  }
}

// Deletes cached hero videos whose URL doesn't match the currently active
// LTO, so the cache doesn't grow forever as campaigns rotate. Pass `null`
// when there's no active LTO (evergreen selected) to clear the whole cache.
export function pruneStaleHeroCache(activeUrl: string | null): void {
  if (Platform.OS !== 'ios') return;
  try {
    const dir = new Directory(Paths.cache, CACHE_SUBDIR);
    if (!dir.exists) return;
    const activeUri = activeUrl ? (heroCacheFile(activeUrl)?.uri ?? null) : null;
    for (const entry of dir.list()) {
      if (!(entry instanceof File)) continue;
      if (entry.uri === activeUri) continue;
      try {
        entry.delete();
      } catch {
        // A single stale file that we can't delete isn't fatal — iOS will
        // reclaim the cache dir under memory pressure anyway. Skip and move
        // on to the next file.
        continue;
      }
    }
  } catch {
    // Directory listing can fail if the cache dir was evicted between checks.
    // That leaves us with an empty cache, which is the desired end state.
    return;
  }
}
